import { DataContext } from '@/types';
import { Data } from '../Data';

export interface ImpersonationOrgData {
  organizationId: string;
  // Organization.Name is nullable server-side, so this can arrive null.
  name: string | null;
  isDemo: boolean;
}

export interface ImpersonationOrg {
  organizationId: string;
  label: string;
  isDemo: boolean;
}

/**
 * The one definition of an organization's picker label. "(DEMO)" used to be appended server-side onto the
 * employee's organization name; the suffix is display-only, so it is built here — and only here, so the
 * dialog's synchronously-derived default label cannot drift from the fetched options.
 */
export function impersonationOrgLabel(name: string | null | undefined, isDemo: boolean): string {
  const safeName = name ?? '';
  return isDemo ? `${safeName} (DEMO)` : safeName;
}

/**
 * The organizations the Switch user dialog may offer. Loaded before any employee so the dialog can
 * fetch one organization's people at a time — it used to derive this list client-side from a
 * platform-wide employee dump, which is what made that request time out.
 */
export class ImpersonationOrgList extends Data {
  private static instance: ImpersonationOrgList | null = null;

  orgs: ImpersonationOrg[] = [];

  constructor(options: any) {
    super({
      api: '/User/ImpersonationOrganizations',
      callback: () => {
        // Cache only once the data is in. Caching the instance up front would make a single failed
        // load permanent: every later open would resolve from an empty cached list.
        ImpersonationOrgList.instance = this;
        options.resolve({ data: this, cached: false });
      },
      ...options,
    });
  }

  onDataReceived(data: ImpersonationOrgData[]) {
    this.orgs = (data ?? [])
      .map((o) => ({
        organizationId: o.organizationId,
        label: impersonationOrgLabel(o.name, o.isDemo),
        isDemo: o.isDemo,
      }))
      // Demo organizations last, then alphabetical — the order the dialog has always shown.
      .sort((a, b) => Number(a.isDemo) - Number(b.isDemo) || a.label.localeCompare(b.label));
  }

  static getOrgs(context: DataContext): Promise<{ data: ImpersonationOrgList; cached: boolean }> {
    return new Promise((resolve, reject) => {
      if (ImpersonationOrgList.instance)
        resolve({ data: ImpersonationOrgList.instance, cached: true });
      else new ImpersonationOrgList({ resolve, reject, context });
    });
  }
}
